import React from "react";
import { Card, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";

function PermissionGroupsOverview() {
  return (
    <div>
      <h2 className="mb-4">
        Permission Groups Overview
      </h2>

      <Alert variant="info">
        <strong>What are Permission Groups?</strong> Permission groups are the foundation of access control in the portal. They let you organize users and control what they can see and do.
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <h4>How Permission Groups Work</h4>
          <p>
            The portal uses a group-based permission system instead of assigning permissions to individual users.
            This makes managing access much easier, especially as your team grows.
          </p>
          <p className="mt-3">
            <strong>The four-step process:</strong>
          </p>
          <ol>
            <li><strong>Create a permission group</strong> (e.g., "Finance Team", "Developers", "Read-Only Users")</li>
            <li><strong>Add permissions to the group</strong> (what actions can they perform?)</li>
            <li><strong>Add users to the group</strong> (who belongs to this group?)</li>
            <li><strong>Add runbooks to the group</strong> (which runbooks can they access?)</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Why Use Groups?</h4>
          <ul>
            <li><strong>Simpler Management</strong> - Instead of setting permissions for each user individually, manage them at the group level</li>
            <li><strong>Consistency</strong> - All users in a group have the same access, preventing accidental over-permissions</li>
            <li><strong>Flexibility</strong> - Users can belong to multiple groups, and permissions are cumulative</li>
            <li><strong>Runbook Control</strong> - Easily restrict specific runbooks to specific teams or departments</li>
            <li><strong>Easy Onboarding</strong> - New team members get the right access by simply adding them to the appropriate group</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Example Scenario</h4>
          <p>
            Let's say you have a Finance Team that should only access finance-related runbooks:
          </p>
          <ol>
            <li>Create a group called "Finance Team"</li>
            <li>Add permissions like <code>runbooks.execute</code>, <code>schedules.view</code>, and <code>history.view</code></li>
            <li>Add all finance team members to the group</li>
            <li>Assign only finance-related runbooks to this group</li>
          </ol>
          <p className="mt-3">
            Now, finance team members can execute and schedule finance runbooks, but they won't see any other department's runbooks.
            When you hire a new finance team member, just add them to the "Finance Team" group and they're all set!
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Accessing Permissions Management</h4>
          <ol>
            <li>Click <strong>Permissions</strong> in the Settings section of the left menu</li>
            <li>You'll see a table showing all permission groups in your organization</li>
            <li>Click on any group to select it and view available actions</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>How User Access Works</h4>
          <p>
            When a user logs in and tries to access the portal:
          </p>
          <ol>
            <li>The system checks which permission groups they belong to</li>
            <li>It collects all permissions from all their groups (cumulative)</li>
            <li>It identifies which runbooks are assigned to any of their groups</li>
            <li>The user sees only those runbooks and can only perform actions they have permissions for</li>
          </ol>
          <Alert variant="warning" className="mt-3">
            <strong>Important:</strong> If a user isn't in any group, or their groups have no runbooks assigned, they won't see any runbooks in the portal.
          </Alert>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Multiple Group Membership</h4>
          <p>
            Users can belong to multiple groups, and this is often useful:
          </p>
          <ul>
            <li>A user could be in both "Finance Team" and "Developers" groups</li>
            <li>They would have access to runbooks from both groups</li>
            <li>They would have all permissions from both groups combined</li>
          </ul>
          <p className="mt-3">
            <strong>Example:</strong> A finance developer could be in "Finance Team" (to access finance runbooks)
            and "Developers" (to have permissions to create and edit runbooks).
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Next Steps</h4>
          <p>Now that you understand permission groups, learn how to:</p>
          <ul>
            <li><Link to="/help/creating-permission-groups">Create Permission Groups</Link></li>
            <li><Link to="/help/assigning-permissions">Assign Permissions to Groups</Link></li>
            <li><Link to="/help/managing-group-members">Add Users to Groups</Link></li>
            <li><Link to="/help/assigning-runbooks-to-groups">Assign Runbooks to Groups</Link></li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  );
}

export default PermissionGroupsOverview;
